import React from "react";
import { Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { RxCross2 } from "react-icons/rx";
import { FaCheckCircle } from "react-icons/fa";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import {
  closeModals,
  openSignIn,
  openSignUp,
} from "../../features/modals/modalSlice";
import { getApiBaseUrl } from "../../apiBaseUrlFinder";

const SignInSchema = Yup.object().shape({
  workEmail: Yup.string().email("Invalid email").required("Required"),
  password: Yup.string().min(6, "Too Short!").required("Required"),
});

const SignUpSchema = Yup.object().shape({
  workEmail: Yup.string().email("Invalid email").required("Required"),
  personalEmail: Yup.string().email("Invalid email"),
  password: Yup.string().min(6, "Too Short!").required("Required"),
});

const AuthModals = ({
  showSignIn,
  showSignUp,
  showSuccessModal,
  setShowSuccessModal,
  showModal,
  setShowModal,
  message,
  showPassword,
  setShowPassword,
  showPasswordSignUp,
  setShowPasswordSignUp,
  submitError,
  onSignIn,
  onSignUp,
  passwordResetModal,
  setPasswordResetModal,
  updatePasswordModal,
  setUpdatePasswordModal,
  showSignupInPasswordReset = true,
}) => {
  const dispatch = useDispatch();

  // Keep Formik's submitting state handled so spinner shows properly on Log In
  const handleSignInSubmit = async (values, helpers) => {
    try {
      await Promise.resolve(onSignIn?.(values, helpers));
    } finally {
      helpers.setSubmitting(false);
    }
  };

  return (
    <>
      {/* Button micro-interactions + spinner (used by Log In; Sign up uses just the animation class) */}
      <style>{`
        .btn-cta{
          transition: transform .12s ease, box-shadow .2s ease, opacity .2s ease;
          will-change: transform;
        }
        .btn-cta:hover{
          transform: translateY(-1px) scale(1.01);
          box-shadow: 0 6px 16px rgba(0,0,0,.12);
        }
        .btn-cta:active{
          transform: translateY(0) scale(.98);
          box-shadow: 0 3px 10px rgba(0,0,0,.10);
        }
        .btn-cta[disabled]{
          opacity:.85; cursor:not-allowed; transform:none; box-shadow:none;
        }

        .btn-cta .spinner{
          width: 16px; height: 16px; margin-left: 8px;
          border: 2px solid rgba(255,255,255,.4);
          border-top-color: currentColor;
          border-radius: 50%;
          animation: spin .8s linear infinite;
          display:inline-block;
          vertical-align: -2px;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        @media (prefers-reduced-motion: reduce){
          .btn-cta{ transition:none; }
          .btn-cta .spinner{ animation: none; }
        }
      `}</style>

      {/* SignIn Modal */}
      <Modal
        show={showSignIn}
        onHide={() => dispatch(closeModals())}
        size="md"
        dialogClassName="custom-modal"
        centered
      >
        <div className="d-flex justify-content-end">
          <div className="px-3 pt-3 pb-3">
            <RxCross2
              onClick={() => dispatch(closeModals())}
              className="#616161"
              fontSize={22}
              style={{ cursor: "pointer" }}
            />
          </div>
        </div>
        <div className="px-lg-5 px-md-4 px-3">
          <div className="signin_modal_border mb-lg-5 mb-md-4 mb-3 mx-lg-2">
            <div>
              <h4 className="text-center signin_modal_h4 pt-lg-4 pt-md-4 pt-3">
                Log In
              </h4>
              <p className="text-center signin_modal_p pt-2">
                Feedback is a feature. It's time to launch <br /> yours.
              </p>
            </div>
            <div className="px-lg-4 px-md-3 px-3">
              <Formik
                initialValues={{ workEmail: "", password: "" }}
                validationSchema={SignInSchema}
                onSubmit={handleSignInSubmit}
              >
                {({ status, isSubmitting }) => (
                  <Form>
                    <div className="form-group mt-3">
                      <Field
                        name="workEmail"
                        placeholder="Work Email"
                        className="custom-input"
                      />
                      <ErrorMessage
                        name="workEmail"
                        component="div"
                        className="text-danger custom-input-error-msg pt-2"
                      />
                    </div>
                    <div className="form-group mt-3">
                      <div className="input-with-toggle">
                        <Field
                          name="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Password"
                          className="custom-input has-toggle"
                        />
                        <button
                          type="button"
                          className="toggle-visibility"
                          aria-label="Toggle password visibility"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                        </button>
                      </div>
                      <ErrorMessage
                        name="password"
                        component="div"
                        className="text-danger custom-input-error-msg pt-2"
                      />
                    </div>
                    <div className="d-flex justify-content-end mt-2 mb-2">
                      <h4
                        className="forgot_password_class"
                        onClick={() => {
                          dispatch(closeModals());
                          setPasswordResetModal(true);
                        }}
                      >
                        Forgot Password
                      </h4>
                    </div>

                    <div className="d-flex justify-content-center mt-5 mb-2">
                      <button
                        type="submit"
                        className="submit_btn_signin btn-cta"
                        disabled={isSubmitting}
                        aria-busy={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            Logging in
                            <span className="spinner" aria-hidden="true" />
                          </>
                        ) : (
                          "Log In"
                        )}
                      </button>
                    </div>
                    {status && (
                      <div
                        className={`text-center mt-3 text-${
                          status.success ? "success" : "danger"
                        }`}
                      >
                        {status.message}
                      </div>
                    )}
                  </Form>
                )}
              </Formik>
            </div>
            <p className="text-center signin_modal_p_bottom pt-2 pb-3">
              Don't have an account?{" "}
              <Link
                onClick={() => dispatch(openSignUp())}
                style={{ color: "black" }}
              >
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </Modal>

      {/* SignUp Modal */}
      <Modal
        show={showSignUp}
        onHide={() => dispatch(closeModals())}
        size="md"
        dialogClassName="custom-modal"
        centered
      >
        <div className="d-flex justify-content-end">
          <div className="px-3 pt-3 pb-3">
            <RxCross2
              onClick={() => dispatch(closeModals())}
              className="#616161"
              fontSize={22}
              style={{ cursor: "pointer" }}
            />
          </div>
        </div>
        <div className="px-lg-5 px-md-4 px-3">
          <div className="signin_modal_border mb-lg-5 mb-md-4 mb-3 mx-lg-2">
            <div>
              <h4 className="text-center signin_modal_h4 pt-lg-4 pt-md-4 pt-3">
                Sign Up
              </h4>
              <p className="text-center signin_modal_p pt-2 pb-1 px-lg-5 px-md-4 px-3">
                Only verified accounts can impact manager ratings. Your data will <span style={{ fontWeight: "bold", color: "black" }}>never</span> be shared with anyone.
              </p>
            </div>
            <div className="px-lg-4 px-md-3 px-3">
              <Formik
                initialValues={{
                  workEmail: "",
                  personalEmail: "",
                  password: "",
                }}
                validationSchema={SignUpSchema}
                onSubmit={onSignUp}
              >
                {({ isSubmitting }) => (
                  <Form>
                    <div className="form-group mt-3">
                      <Field
                        name="workEmail"
                        placeholder="Work Email"
                        className="custom-input"
                      />
                      <ErrorMessage
                        name="workEmail"
                        component="div"
                        className="text-danger custom-input-error-msg pt-2"
                      />
                    </div>

                    <div className="form-group mt-3">
                      <Field
                        name="personalEmail"
                        placeholder="Personal Email (optional)"
                        className="custom-input"
                      />
                      <ErrorMessage
                        name="personalEmail"
                        component="div"
                        className="text-danger custom-input-error-msg pt-2"
                      />
                    </div>
                    <div className="form-group mt-3">
                      <div className="input-with-toggle">
                        <Field
                          name="password"
                          type={showPasswordSignUp ? "text" : "password"}
                          placeholder="Password"
                          className="custom-input has-toggle"
                        />
                        <button
                          type="button"
                          className="toggle-visibility"
                          aria-label="Toggle password visibility"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => setShowPasswordSignUp(!showPasswordSignUp)}
                        >
                          {showPasswordSignUp ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                        </button>
                      </div>
                      <ErrorMessage
                        name="password"
                        component="div"
                        className="text-danger custom-input-error-msg pt-2"
                      />
                    </div>
                    <div className="mt-5 mb-2">
                      <div className="d-flex justify-content-center">
                        {/* Add hover/click animation here via btn-cta */}
                        <button
                          type="submit"
                          className="submit_btn_signin btn-cta"
                          disabled={isSubmitting}
                          aria-busy={isSubmitting}
                        >
                          {isSubmitting ? "Submitting..." : "Sign up"}
                        </button>
                      </div>
                      {submitError && (
                        <div className="d-flex justify-content-center text-danger mt-3 text-center">
                          {submitError}
                        </div>
                      )}
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
            <p className="text-center signin_modal_p_bottom pt-2 pb-3">
              Already have an account?{" "}
              <Link
                onClick={() => dispatch(openSignIn())}
                style={{ color: "black" }}
              >
                Sign In
              </Link>
            </p>
            <p className="text-center signin_modal_p pt-2 pb-1 px-lg-5 px-md-4 px-3" style={{ fontSize: "0.65rem" }}>
              We use your work email solely to confirm your employment. Your information is encrypted and securely protected.
            </p>
          </div>
        </div>
      </Modal>

      {/* Verification Success Modal */}
      <Modal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        centered
      >
        <div className="text-center p-4 my-4">
          <FaCheckCircle size={60} color="green" className="mb-3" />
          <h5 className="mb-2">Verification Email Sent</h5>
          <p className="text-muted mb-3" style={{ fontSize: "14px" }}>
            Please check your work email's inbox...
          </p>
          <button
            className="btn btn-success px-4"
            onClick={() => setShowSuccessModal(false)}
          >
            OK
          </button>
        </div>
      </Modal>

      {/* Forgot Password Modal */}
      <Modal
        show={passwordResetModal}
        onHide={() => setPasswordResetModal(false)}
        centered
      >
        <div className="text-center px-4 pt-4">
          <h5 className="mb-3">Password Reset</h5>
          <p className="text-muted mb-3" style={{ fontSize: "14px" }}>
            An email will be sent to your email address with steps on resetting
            your password.
          </p>
        </div>

        <div className="px-4 pb-4 mx-lg-4">
          <Formik
            initialValues={{ work_email: "" }}
            validate={(values) => {
              const errors = {};
              if (!values.work_email) {
                errors.work_email = "Work Email is required";
              } else if (
                !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\\.[A-Z]{2,}$/i.test(values.work_email)
              ) {
                errors.work_email = "Invalid email address";
              }
              return errors;
            }}
            onSubmit={async (values, { setSubmitting, setStatus }) => {
              const apiUrl = `${getApiBaseUrl()}`;
              try {
                const res = await fetch(`${apiUrl}/reset_password`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ work_email: values.work_email }),
                });
                const data = await res.json();
                if (!res.ok) throw new Error(data.message || "Request failed");
                setStatus({ success: true, message: data.message });
                setTimeout(() => setPasswordResetModal(false), 5000);
              } catch (err) {
                setStatus({
                  success: false,
                  message: err.message || "Failed to send reset link.",
                });
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ isSubmitting, status }) => (
              <Form>
                <div className="form-group py-5">
                  <Field
                    type="email"
                    name="work_email"
                    placeholder="Enter your work email"
                    className="custom-input"
                  />
                  <ErrorMessage
                    name="work_email"
                    component="div"
                    className="text-danger custom-input-error-msg pt-2"
                  />
                </div>

                {status?.message && (
                  <div className={`mt-2 ${status.success ? "text-success" : "text-danger"}`}>
                    {status.message}
                  </div>
                )}

                <div className="d-flex justify-content-center">
                  <button
                    type="submit"
                    className="submit_btn_signin py-2 btn-cta"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Sending..." : "Reset Password"}
                  </button>
                </div>
                {showSignupInPasswordReset && (
                  <p className="text-center signin_modal_p_bottom pt-4 pb-3">
                    Don't have an account?{" "}
                    <Link
                      onClick={() => {
                        dispatch(closeModals());
                        dispatch(openSignUp());
                      }}
                      style={{ color: "black" }}
                    >
                      Sign up
                    </Link>
                  </p>
                )}
              </Form>
            )}
          </Formik>
        </div>
      </Modal>

      {/* Update Password Modal (hidden content left as in your original) */}
      <Modal
        show={updatePasswordModal}
        onHide={() => setUpdatePasswordModal(false)}
        centered
      >
        <div className="text-center px-4 pt-4">
          <h5 className="mb-3">Update Password</h5>
        </div>
        <div className="px-4 pb-4 mx-lg-4 d-none" />
      </Modal>

      {/* Account Verified Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Body>
          <div className="p-4">
            <div className="text-center">
              <h4 className="account_verified_text">Account Verified!</h4>
            </div>
            <div className="text-center pt-3">
              <button
                className="login_button_modal"
                onClick={() => {
                  setShowModal(false);
                  dispatch(openSignIn());
                }}
              >
                Login
              </button>
              <p
                className="p_without_signin pt-4"
                onClick={() => setShowModal(false)}
              >
                continue without signing in
              </p>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AuthModals;